export default {
  slug: "/sapphire-hair-transplant",
  group: { href: "/hair", label: { en: "Hair restoration", ar: "زراعة الشعر" } },
  heroImage: "/assets/images/ai/services/hair/hero.webp",

  en: {
    title: "Sapphire FUE Hair Transplant in Istanbul",
    eyebrow: "Hair restoration",
    lead:
      "Sapphire FUE is the same follicular-unit extraction as standard FUE, but the channels for the grafts " +
      "are opened with sapphire blades instead of steel. The difference is in the incisions, not the follicles.",
    cta: "Book a free assessment",
    heroAlt: "Sapphire FUE hair transplant planning at Platinya Clinic",
    stats: [
      { value: "6–8 h", label: "Procedure time" },
      { value: "1 day", label: "At the clinic" },
      { value: "10–14 days", label: "Scabs gone" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "Sapphire is a technique, not an eligibility test: the same rules apply as for any transplant — " +
        "stable loss and enough donor hair.",
      items: [
        "Stable hair loss with a healthy donor area at the back and sides",
        "Thinning at the hairline, temples or crown that medical treatment alone has not addressed",
        "Skin without active infection, eczema or psoriasis in the recipient area",
        "Understanding that the blade changes healing, not the number of grafts you can afford",
      ],
      note:
        "Clinics that advertise sapphire as a guarantee of denser hair are overselling the instrument. " +
        "Graft survival depends on how the follicles are handled, not on the blade alone.",
    },
    techniques: {
      heading: "What the sapphire blade changes",
      intro: "The same operation, with a different cutting edge and a different set of trade-offs.",
      items: [
        {
          name: "Narrower channels",
          text:
            "Sapphire blades cut a finer slit than steel, so the tissue is separated rather than torn. " +
            "Smaller openings mean less crusting and, in most patients, faster healing.",
        },
        {
          name: "Tighter graft fit",
          text:
            "A graft that sits snugly in its channel is less likely to move in the first days. " +
            "That matters most in the hairline, where direction and angle are visible.",
        },
        {
          name: "What it does not change",
          text:
            "It does not increase the number of grafts your donor area can give, and it does not replace " +
            "the plan: density, hairline design and how the team handles the follicles decide the result.",
        },
      ],
    },
    journey: {
      heading: "The day, hour by hour",
      steps: [
        {
          name: "Design and shaving",
          text:
            "The hairline is drawn and agreed before any cutting. The donor area is trimmed short; " +
            "the recipient area is shaved in most cases.",
        },
        {
          name: "Anaesthesia",
          text: "Local anaesthetic injections with light sedation. Discomfort is limited to the first minutes.",
        },
        {
          name: "Extraction and channels",
          text:
            "Follicles are extracted, kept cool in solution, and the sapphire channels are opened at the " +
            "angle the hairline needs.",
        },
        {
          name: "Implantation",
          text: "Grafts are placed one by one. The team works in shifts on longer sessions to keep handling consistent.",
        },
        {
          name: "Healing",
          text:
            "Tiny crusts fall away in 10–14 days. The transplanted hair sheds from week three, then regrows " +
            "from month three onward.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro: "Every transplant carries the same core risks, whatever the blade.",
      items: [
        "Swelling, tenderness and temporary numbness in the donor and recipient areas",
        "Crusting and redness that last longer than expected in sensitive skin",
        "Some grafts do not survive, which is why a second session is sometimes needed",
        "Folliculitis or small cysts around new grafts in the first weeks",
        "A hairline that looks too straight or too dense if the design was not conservative",
      ],
      note:
        "Ask how many grafts are planned, who opens the channels and who implants — the answers say more " +
        "about the result than the word 'sapphire'.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Is sapphire better than steel FUE?",
          a:
            "It heals a little faster and produces less crusting. Independent evidence that it grows more " +
            "hair is weak; the surgeon's handling of the grafts matters more.",
        },
        {
          q: "Does it hurt more?",
          a: "No. The anaesthesia is the same; the blade is used on numbed skin.",
        },
        {
          q: "How soon do the scabs disappear?",
          a: "Usually within 10–14 days with gentle washing from the second day.",
        },
        {
          q: "Is one session enough?",
          a:
            "For many patients yes. Large bald areas or low donor density may need a second session after " +
            "12 months to reach the planned density.",
        },
      ],
    },
    ctaBand: {
      heading: "Send your photographs for a first opinion",
      text: "We will tell you honestly whether sapphire FUE adds anything in your case, and what the graft count would be.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability is decided after examination; results vary between patients.",
  },

  ar: {
    title: "زراعة الشعر بطريقة السفير في إسطنبول",
    eyebrow: "زراعة الشعر",
    lead:
      "السفير FUE هي نفس تقنية اقتطاف البصيلات، لكن قنوات الزرع تُفتح بشفرات سفير بدل الفولاذ. " +
      "الفرق في الشقوق لا في البصيلات.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "تخطيط زراعة الشعر بطريقة السفير في عيادة بلاتينيا",
    stats: [
      { value: "٦–٨ ساعات", label: "مدة الجلسة" },
      { value: "يوم واحد", label: "في العيادة" },
      { value: "١٠–١٤ يوماً", label: "زوال القشور" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "السفير تقنية لا اختبار أهلية: تنطبق نفس القواعد كأي زراعة — تساقط مستقر وشعر مانح كافٍ.",
      items: [
        "تساقط مستقر مع منطقة مانحة سليمة في المؤخرة والجانبين",
        "خفّة في خط الشعر أو الصدغين أو التاج لم يعالجها الدواء وحده",
        "جلد سليم بلا التهاب نشط أو أكزيما أو صدفية في المنطقة المزروعة",
        "إدراك أن الشفرة تغيّر الشفاء، لا عدد البصيلات التي تستطيع دفعه",
      ],
      note:
        "العيادات التي تسوّق السفير كضمان لكثافة أعلى تبالغ في وصف الأداة. بقاء البصيلات يعتمد على طريقة " +
        "التعامل معها لا على الشفرة وحدها.",
    },
    techniques: {
      heading: "ما الذي تغيّره شفرة السفير",
      intro: "نفس العملية، بحدّ قطع مختلف ومقايضات مختلفة.",
      items: [
        {
          name: "قنوات أضيق",
          text:
            "شفرات السفير تقطع شقاً أدقّ من الفولاذ، فينفصل النسيج بدل أن يتمزّق. الفتحات الأصغر تعني " +
            "قشوراً أقل وشفاءً أسرع عند معظم المرضى.",
        },
        {
          name: "ثبات أفضل للبصيلة",
          text:
            "البصيلة التي تستقرّ بإحكام في قناتها تتحرّك أقل في الأيام الأولى. وهذا يهمّ أكثر في خط الشعر " +
            "حيث الاتجاه والزاوية ظاهران.",
        },
        {
          name: "ما لا تغيّره",
          text:
            "لا تزيد عدد البصيلات التي تستطيع منطقتك المانحة تقديمها، ولا تعوّض الخطة: الكثافة وتصميم " +
            "خط الشعر وطريقة تعامل الفريق مع البصيلات هي ما يحدّد النتيجة.",
        },
      ],
    },
    journey: {
      heading: "يوم العملية ساعة بساعة",
      steps: [
        {
          name: "التصميم والحلاقة",
          text: "يُرسم خط الشعر ويُتفق عليه قبل أي قصّ. تُقصّر المنطقة المانحة، وتُحلق المنطقة المزروعة في معظم الحالات.",
        },
        {
          name: "التخدير",
          text: "إبر تخدير موضعي مع تهدئة خفيفة. الانزعاج محصور في الدقائق الأولى.",
        },
        {
          name: "الاقتطاف وفتح القنوات",
          text: "تُقتطف البصيلات وتُحفظ باردة في محلول، وتُفتح قنوات السفير بالزاوية التي يحتاجها خط الشعر.",
        },
        {
          name: "الزرع",
          text: "تُزرع البصيلات واحدة واحدة. يعمل الفريق بنوبات في الجلسات الطويلة للحفاظ على ثبات التعامل.",
        },
        {
          name: "الشفاء",
          text:
            "تتساقط القشور الدقيقة خلال ١٠–١٤ يوماً. الشعر المزروع يتساقط من الأسبوع الثالث ثم ينمو من الشهر الثالث.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro: "كل زراعة تحمل المخاطر الأساسية نفسها، أيّاً كانت الشفرة.",
      items: [
        "تورّم وحساسية وتنميل مؤقّت في المنطقة المانحة والمزروعة",
        "قشور واحمرار يستمرّان أطول من المتوقّع في الجلد الحسّاس",
        "بعض البصيلات لا تعيش، ولهذا تحتاج جلسة ثانية أحياناً",
        "التهاب الجريبات أو أكياس صغيرة حول البصيلات الجديدة في الأسابيع الأولى",
        "خط شعر يبدو مستقيماً أو كثيفاً أكثر من اللازم إذا لم يكن التصميم متحفّظاً",
      ],
      note:
        "اسأل: كم بصيلة مخطّطة، ومن يفتح القنوات ومن يزرع — الجواب يقول عن النتيجة أكثر من كلمة «سفير».",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "هل السفير أفضل من الفولاذ؟",
          a:
            "يشفي أسرع قليلاً ويُنتج قشوراً أقل. الأدلّة المستقلّة على أنه ينبت شعراً أكثر ضعيفة؛ " +
            "طريقة تعامل الجرّاح مع البصيلات أهمّ.",
        },
        {
          q: "هل هو أكثر إيلاماً؟",
          a: "لا. التخدير نفسه، والشفرة تُستخدم على جلد مخدّر.",
        },
        {
          q: "متى تزول القشور؟",
          a: "عادة خلال ١٠–١٤ يوماً مع غسل لطيف من اليوم الثاني.",
        },
        {
          q: "هل تكفي جلسة واحدة؟",
          a:
            "لكثير من المرضى نعم. المناطق الفارغة الكبيرة أو ضعف المنطقة المانحة قد تحتاج جلسة ثانية " +
            "بعد ١٢ شهراً للوصول للكثافة المخطّطة.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورك لرأي أوّلي",
      text: "سنقول بصراحة هل يضيف السفير شيئاً في حالتك، وكم سيكون عدد البصيلات.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. تُحدَّد الملاءمة بعد الفحص؛ والنتائج تختلف من مريض لآخر.",
  },
};
